import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { manualApi } from '../../api/manual';
import { buildingsApi } from '../../api/buildings';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

export default function ManualSubmissionPanel({ buildingId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState({
    manual_view_only: false,
    manual_submission_enabled: false,
    mobile_view_only: false,
    mobile_submission_enabled: false,
    manual_allow_account_registration: false,
    manual_allow_code_registration: false,
    manual_allow_meter_pair_registration: false,
    manual_submission_instructions: '',
    manual_contact_info: '',
    manual_submission_day_from: '',
    manual_submission_day_to: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    apartment_id: '',
    code: '',
    label: '',
    expires_at: '',
    max_uses: 1,
  });
  const [creatingToken, setCreatingToken] = useState(false);
  const [error, setError] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadOverview = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const data = await manualApi.getBuildingOverview(String(buildingId));
      setOverview(data);
      setSettings({
        manual_view_only: !!data.manual_view_only,
        manual_submission_enabled: !!data.manual_submission_enabled,
        mobile_view_only: !!data.mobile_view_only,
        mobile_submission_enabled: !!data.mobile_submission_enabled,
        manual_allow_account_registration: !!data.manual_allow_account_registration,
        manual_allow_code_registration: !!data.manual_allow_code_registration,
        manual_allow_meter_pair_registration: !!data.manual_allow_meter_pair_registration,
        manual_submission_instructions: data.manual_submission_instructions || '',
        manual_contact_info: data.manual_contact_info || '',
        manual_submission_day_from: data.manual_submission_day_from
          ? String(data.manual_submission_day_from)
          : '',
        manual_submission_day_to: data.manual_submission_day_to
          ? String(data.manual_submission_day_to)
          : '',
      });
    } catch (err) {
      setError('Не удалось загрузить настройки ручной передачи');
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const parseDayValue = (value) => {
    if (value === '' || value === undefined || value === null) {
      return null;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return null;
    }
    return parsed;
  };

  const handleSaveSettings = async () => {
    if (!buildingId) return false;
    setSavingSettings(true);
    try {
      const dayFrom = parseDayValue(settings.manual_submission_day_from);
      const dayTo = parseDayValue(settings.manual_submission_day_to);
      await buildingsApi.updateBuilding(String(buildingId), {
        manual_view_only: settings.manual_view_only,
        manual_submission_enabled: settings.manual_view_only ? false : settings.manual_submission_enabled,
        mobile_view_only: settings.mobile_view_only,
        mobile_submission_enabled: settings.mobile_view_only
          ? false
          : settings.mobile_submission_enabled,
        manual_allow_account_registration: settings.manual_allow_account_registration,
        manual_allow_code_registration: settings.manual_allow_code_registration,
        manual_allow_meter_pair_registration: settings.manual_allow_meter_pair_registration,
        manual_submission_instructions: settings.manual_submission_instructions || null,
        manual_contact_info: settings.manual_contact_info || null,
        manual_submission_day_from: dayFrom,
        manual_submission_day_to: dayTo,
      });
      await loadOverview();
      return true;
    } catch (err) {
      setError('Не удалось сохранить настройки');
      return false;
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCreateToken = async () => {
    if (!tokenForm.apartment_id || !tokenForm.code) return;
    setCreatingToken(true);
    try {
      await manualApi.createToken({
        apartment_id: tokenForm.apartment_id,
        code: tokenForm.code,
        label: tokenForm.label || undefined,
        expires_at: tokenForm.expires_at || undefined,
        max_uses: tokenForm.max_uses || 1,
      });
      setTokenForm({
        apartment_id: '',
        code: '',
        label: '',
        expires_at: '',
        max_uses: 1,
      });
      await loadOverview();
    } catch (err) {
      setError('Не удалось создать токен');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Удалить токен?')) return;
    try {
      await manualApi.deleteToken(tokenId);
      await loadOverview();
    } catch (err) {
      setError('Не удалось удалить токен');
    }
  };

  const handleRevokeBinding = async (bindingId) => {
    if (!window.confirm('Отключить пользователя от квартиры?')) return;
    try {
      await manualApi.revokeBinding(bindingId);
      await loadOverview();
    } catch (err) {
      setError('Не удалось отключить пользователя');
    }
  };

  const apartments = overview?.apartments || [];

  const viewOnlyMode = settings.manual_view_only;
  const manualEnabled = settings.manual_submission_enabled && !viewOnlyMode;
  const mobileViewOnlyMode = settings.mobile_view_only;
  const mobileEnabled =
    settings.mobile_submission_enabled && !mobileViewOnlyMode;
  const bindingCount = overview?.bindings?.length || 0;
  const tokenCount = overview?.tokens?.length || 0;
  const windowFrom = settings.manual_submission_day_from || '—';
  const windowTo = settings.manual_submission_day_to || '—';
  const windowSummary = viewOnlyMode
    ? 'Режим только просмотра'
    : manualEnabled
    ? `Окно подачи: ${windowFrom} – ${windowTo}`
    : 'Ручная подача отключена';
  const mobileSummary = mobileViewOnlyMode
    ? 'Только просмотр в мобильном приложении'
    : mobileEnabled
    ? `Мобильная подача включена (${windowFrom} – ${windowTo})`
    : 'Мобильная подача отключена';
  const instructionsHint = settings.manual_submission_instructions
    ? `${settings.manual_submission_instructions.slice(0, 80)}${
        settings.manual_submission_instructions.length > 80 ? '…' : ''
      }`
    : 'Инструкции ещё не заданы';
  const contactHint = settings.manual_contact_info
    ? `${settings.manual_contact_info.slice(0, 80)}${
        settings.manual_contact_info.length > 80 ? '…' : ''
      }`
    : 'Контакты ещё не заданы';

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <div>
            <Typography variant="h6">Ручная передача показаний</Typography>
            <Typography variant="body2" color="text.secondary">
              {windowSummary} · Подключений: {bindingCount} · Активных токенов: {tokenCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mobileSummary}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {instructionsHint}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Контакты УК: {contactHint}
            </Typography>
          </div>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={
                viewOnlyMode ? 'Только просмотр' : manualEnabled ? 'Активна' : 'Выключена'
              }
              color={viewOnlyMode ? 'warning' : manualEnabled ? 'success' : 'default'}
              variant={viewOnlyMode || manualEnabled ? 'filled' : 'outlined'}
              size="small"
            />
            <Chip
              label={
                mobileViewOnlyMode
                  ? 'Моб: только просмотр'
                  : mobileEnabled
                  ? 'Моб: активна'
                  : 'Моб: выключена'
              }
              color={
                mobileViewOnlyMode ? 'warning' : mobileEnabled ? 'success' : 'default'
              }
              variant={mobileViewOnlyMode || mobileEnabled ? 'filled' : 'outlined'}
              size="small"
            />
            <Tooltip title="Открыть настройки">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => setSettingsDialogOpen(true)}
                  disabled={loading}
                >
                  <SettingsIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant={detailsOpen ? 'contained' : 'outlined'}
              onClick={() => setDetailsOpen((prev) => !prev)}
            >
              {detailsOpen ? 'Скрыть панель' : 'Управление'}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
          <Stack spacing={3}>
            {error ? <Typography color="error">{error}</Typography> : null}

            <Divider />
            <Typography variant="subtitle1">Создание одноразового кода</Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <TextField
                select
                label="Квартира"
                value={tokenForm.apartment_id}
                onChange={(e) =>
                  setTokenForm((prev) => ({ ...prev, apartment_id: e.target.value }))
                }
                sx={{ minWidth: 200 }}
              >
                {apartments.map((apt) => (
                  <MenuItem key={apt.id} value={apt.id}>
                    Кв. {apt.number} {apt.corp_name ? `(${apt.corp_name})` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Код"
                value={tokenForm.code}
                onChange={(e) =>
                  setTokenForm((prev) => ({ ...prev, code: e.target.value }))
                }
              />
              <TextField
                label="Комментарий"
                value={tokenForm.label}
                onChange={(e) =>
                  setTokenForm((prev) => ({ ...prev, label: e.target.value }))
                }
              />
              <TextField
                label="Дата истечения (YYYY-MM-DD HH:MM)"
                value={tokenForm.expires_at}
                onChange={(e) =>
                  setTokenForm((prev) => ({ ...prev, expires_at: e.target.value }))
                }
              />
              <TextField
                type="number"
                label="Количество использований"
                value={tokenForm.max_uses}
                onChange={(e) =>
                  setTokenForm((prev) => ({ ...prev, max_uses: Number(e.target.value) }))
                }
                sx={{ width: 200 }}
              />
              <Button
                variant="outlined"
                onClick={handleCreateToken}
                disabled={creatingToken}
              >
                Создать код
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Активные коды
            </Typography>
            <Stack spacing={1}>
              {(overview?.tokens || []).map((token) => (
                <Paper
                  key={token.id}
                  variant="outlined"
                  sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <strong>{token.code_hint}</strong> — использовано {token.use_count}/{token.max_uses}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => token.apartment_id && navigate(`/apartments/${token.apartment_id}`)}
                      sx={{ p: 0, minWidth: 0, fontSize: 12, textTransform: 'none', color: '#2368B5' }}
                    >
                      Квартира: {apartments.find((a) => a.id === token.apartment_id)?.number || token.apartment_id}
                    </Button>
                  </div>
                  <div>
                    <Button size="small" color="error" onClick={() => handleDeleteToken(token.id)}>
                      Удалить
                    </Button>
                  </div>
                </Paper>
              ))}
              {!overview?.tokens?.length && <Typography color="text.secondary">Коды не созданы</Typography>}
            </Stack>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Подключённые пользователи Telegram
            </Typography>
            <Stack spacing={1}>
              {(overview?.bindings || []).map((binding) => (
                <Paper
                  key={binding.id}
                  variant="outlined"
                  sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div>
                    <div>
                      @{binding.telegram_username || 'не указано'} — {binding.telegram_id}
                    </div>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => binding.apartment_id && navigate(`/apartments/${binding.apartment_id}`)}
                      sx={{ p: 0, minWidth: 0, fontSize: 12, textTransform: 'none', color: '#2368B5' }}
                    >
                      Квартира {binding.apartment_number || binding.apartment_id} · статус {binding.status}
                    </Button>
                  </div>
                  <Button size="small" color="error" onClick={() => handleRevokeBinding(binding.id)}>
                    Отключить
                  </Button>
                </Paper>
              ))}
              {!overview?.bindings?.length && (
                <Typography color="text.secondary">У этого дома пока нет зарегистрированных пользователей</Typography>
              )}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
      <ManualSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        settings={settings}
        setSettings={setSettings}
        onSave={handleSaveSettings}
        saving={savingSettings}
        disabled={loading}
      />
    </Paper>
  );
}

function MarkdownToolbar({ onBold, onItalic, onList, onLink, disabled }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
      <Button size="small" variant="outlined" onClick={onBold} disabled={disabled}>
        <strong>B</strong>
      </Button>
      <Button size="small" variant="outlined" onClick={onItalic} disabled={disabled}>
        <em>I</em>
      </Button>
      <Button size="small" variant="outlined" onClick={onList} disabled={disabled}>
        •
      </Button>
      <Button size="small" variant="outlined" onClick={onLink} disabled={disabled}>
        🔗
      </Button>
    </Stack>
  );
}

function MarkdownPreview({ value }) {
  const hasValue = Boolean(value?.trim());
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Предпросмотр
      </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflowY: 'auto' }}>
        {hasValue ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
            {value}
          </ReactMarkdown>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Текст пока не задан
          </Typography>
        )}
      </Paper>
    </Stack>
  );
}

function ManualSettingsDialog({
  open,
  onClose,
  settings,
  setSettings,
  onSave,
  saving,
  disabled,
}) {
  const handleSwitchChange = (field) => (event) => {
    const { checked } = event.target;
    setSettings((prev) => ({ ...prev, [field]: checked }));
  };

  const handleViewOnlyChange = (event) => {
    const { checked } = event.target;
    setSettings((prev) => ({
      ...prev,
      manual_view_only: checked,
      manual_submission_enabled: checked ? false : prev.manual_submission_enabled,
    }));
  };

  const handleMobileViewOnlyChange = (event) => {
    const { checked } = event.target;
    setSettings((prev) => ({
      ...prev,
      mobile_view_only: checked,
      mobile_submission_enabled: checked ? false : prev.mobile_submission_enabled,
    }));
  };

  const handleDayChange = (field) => (event) => {
    const { value } = event.target;
    if (value === '') {
      setSettings((prev) => ({ ...prev, [field]: '' }));
      return;
    }
    const parsed = Math.max(1, Math.min(31, Number(value)));
    if (Number.isNaN(parsed)) {
      return;
    }
    setSettings((prev) => ({ ...prev, [field]: String(parsed) }));
  };

  const handleSubmit = async () => {
    const ok = await onSave();
    if (ok) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Настройка ручной подачи показаний</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Telegram-бот</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.manual_view_only}
                onChange={handleViewOnlyChange}
              />
            }
            label="Только просмотр (Telegram-бот выключен)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.manual_submission_enabled}
                onChange={handleSwitchChange('manual_submission_enabled')}
                disabled={settings.manual_view_only}
              />
            }
            label="Включить Telegram-бота"
          />
          <Divider />
          <Typography variant="subtitle2">Мобильное приложение</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.mobile_view_only}
                onChange={handleMobileViewOnlyChange}
              />
            }
            label="Только просмотр (мобильная подача отключена)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.mobile_submission_enabled}
                onChange={handleSwitchChange('mobile_submission_enabled')}
                disabled={settings.mobile_view_only}
              />
            }
            label="Включить мобильную подачу"
          />
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.manual_allow_account_registration}
                  onChange={handleSwitchChange('manual_allow_account_registration')}
                />
              }
              label="Разрешить регистрацию по лицевому счёту"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.manual_allow_code_registration}
                  onChange={handleSwitchChange('manual_allow_code_registration')}
                />
              }
              label="Разрешить регистрацию по одноразовому коду"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.manual_allow_meter_pair_registration}
                  onChange={handleSwitchChange('manual_allow_meter_pair_registration')}
                />
              }
              label="Разрешить связку по квартире и счётчику"
            />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <TextField
              label="День начала окна приёма (1-31)"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={settings.manual_submission_day_from}
              onChange={handleDayChange('manual_submission_day_from')}
              disabled={
                !(
                  settings.manual_submission_enabled ||
                  settings.mobile_submission_enabled
                )
              }
              helperText="Например, 25 — жильцы передают показания с 25 числа"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="День закрытия окна (1-31)"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={settings.manual_submission_day_to}
              onChange={handleDayChange('manual_submission_day_to')}
              disabled={
                !(
                  settings.manual_submission_enabled ||
                  settings.mobile_submission_enabled
                )
              }
              helperText="Например, 31 — приём показаний закрывается"
              sx={{ minWidth: 200 }}
            />
          </Stack>
          <MarkdownEditor
            label="Инструкция для жильцов"
            value={settings.manual_submission_instructions}
            onChange={(value) =>
              setSettings((prev) => ({
                ...prev,
                manual_submission_instructions: value,
              }))
            }
            minRows={3}
            disabled={disabled || saving}
          />
          <MarkdownEditor
            label="Контактная информация УК"
            value={settings.manual_contact_info}
            onChange={(value) =>
              setSettings((prev) => ({
                ...prev,
                manual_contact_info: value,
              }))
            }
            minRows={2}
            helperText="Телефоны, график работы, ссылки на ЛК, аварийные контакты"
            disabled={disabled || saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || disabled}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MarkdownEditor({ label, value, onChange, minRows = 3, helperText, disabled }) {
  const [mode, setMode] = useState('preview');
  const textareaRef = useRef(null);

  const updateValue = (formatter) => {
    const field = textareaRef.current;
    if (!field) return;
    const currentValue = value || '';
    const start = field.selectionStart ?? currentValue.length;
    const end = field.selectionEnd ?? start;
    const selected = currentValue.slice(start, end);
    const result = formatter({ value: currentValue, start, end, selected });
    if (!result) return;
    const { nextValue, selectionStart, selectionEnd } = result;
    onChange(nextValue);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const wrap = (prefix, suffix, placeholder) => () =>
    updateValue(({ value: currentValue, start, end, selected }) => {
      const text = selected || placeholder;
      const before = currentValue.slice(0, start);
      const after = currentValue.slice(end);
      const nextValue = `${before}${prefix}${text}${suffix}${after}`;
      const selectionStart = start + prefix.length;
      const selectionEnd = selectionStart + text.length;
      return { nextValue, selectionStart, selectionEnd };
    });

  const makeList = () =>
    updateValue(({ value: currentValue, start, end, selected }) => {
      const block = selected || '\u043f\u0443\u043d\u043a\u0442 \u0441\u043f\u0438\u0441\u043a\u0430';
      const formatted = block
        .split(/\r?\n/)
        .map((line) => {
          const trimmed = line.trim();
          if (!trimmed) return '- ';
          return trimmed.startsWith('- ') ? trimmed : `- ${trimmed}`;
        })
        .join('\n');
      const before = currentValue.slice(0, start);
      const after = currentValue.slice(end);
      const nextValue = `${before}${formatted}${after}`;
      return { nextValue, selectionStart: start, selectionEnd: start + formatted.length };
    });

  const makeLink = () =>
    updateValue(({ value: currentValue, start, end, selected }) => {
      const textValue = selected || '\u0442\u0435\u043a\u0441\u0442 \u0441\u0441\u044b\u043b\u043a\u0438';
      const url = 'https://example.com';
      const insertion = `[${textValue}](${url})`;
      const before = currentValue.slice(0, start);
      const after = currentValue.slice(end);
      const nextValue = `${before}${insertion}${after}`;
      const hrefIndex = insertion.indexOf(url);
      const selectionStart = start + hrefIndex;
      const selectionEnd = selectionStart + url.length;
      return { nextValue, selectionStart, selectionEnd };
    });

  const toolbarDisabled = disabled || mode !== 'edit';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">{label}</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next) => {
              if (next) {
                setMode(next);
              }
            }}
          >
            <ToggleButton value="edit">Редактировать</ToggleButton>
            <ToggleButton value="preview">Предпросмотр</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <MarkdownToolbar
          disabled={toolbarDisabled}
          onBold={wrap('**', '**', 'жирный текст')}
          onItalic={wrap('_', '_', 'курсив')}
          onList={makeList}
          onLink={makeLink}
        />
        {mode === 'edit' ? (
          <TextField
            multiline
            minRows={minRows}
            inputRef={textareaRef}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            helperText={helperText}
            disabled={disabled}
          />
        ) : (
          <MarkdownPreview value={value} helperText={helperText} />
        )}
      </Stack>
    </Paper>
  );
}
